import {
  findPeriodByMob,
  getCohortPeriodValue,
} from './cohortSheetPeriodValue';

const DEFAULT_ROAS_AVG_COHORTS = 3;
const ROAS_100_THRESHOLD = 1;
export const COHORT_ROAS_AVG_WINDOW_MIN = 3;
export const COHORT_ROAS_AVG_WINDOW_MAX = 12;

function sampleStdev(values) {
  if (!values?.length || values.length < 2) {
    return null;
  }
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance =
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
    (values.length - 1);
  return Math.sqrt(variance);
}

function collectRoasValuesForMob(cohorts, startIndex, mob) {
  const values = [];
  for (let rowIndex = startIndex; rowIndex < cohorts.length; rowIndex += 1) {
    const rawSpend = cohorts[rowIndex].marketingSpend;
    if (rawSpend != null && Number(rawSpend) <= 0) {
      continue;
    }
    const period = findPeriodByMob(cohorts[rowIndex].periods, mob);
    if (!period || period.roas == null) {
      continue;
    }
    values.push(Number(period.roas) || 0);
  }
  return values;
}

function periodCumulativeRoas(period, marketingSpend) {
  if (period?.roas != null && Number.isFinite(Number(period.roas))) {
    return Number(period.roas);
  }
  const spend = Number(marketingSpend) || 0;
  if (spend <= 0 || period?.revenue == null) {
    return null;
  }
  return (Number(period.revenue) || 0) / spend;
}

function maxCumulativeRoasForCohort(cohort, { actualOnly = false } = {}) {
  const marketingSpend = Number(cohort?.marketingSpend) || 0;
  if (marketingSpend <= 0) {
    return null;
  }

  let maxRoas = 0;
  let hasValue = false;
  for (const period of cohort?.periods ?? []) {
    if (actualOnly && period.isPredicted) {
      continue;
    }
    const cumulativeRoas = periodCumulativeRoas(period, marketingSpend);
    if (cumulativeRoas == null) {
      continue;
    }
    hasValue = true;
    maxRoas = Math.max(maxRoas, cumulativeRoas);
  }

  return hasValue ? maxRoas : null;
}

export function getCohortRoas100Counts(cohortSheet) {
  const cohorts = cohortSheet?.cohorts ?? [];
  let actualAtOrAbove100 = 0;
  let predictedToReach100 = 0;

  for (const cohort of cohorts) {
    const actualMax = maxCumulativeRoasForCohort(cohort, { actualOnly: true });
    const fullMax = maxCumulativeRoasForCohort(cohort, { actualOnly: false });

    if (actualMax != null && actualMax >= ROAS_100_THRESHOLD) {
      actualAtOrAbove100 += 1;
    } else if (fullMax != null && fullMax >= ROAS_100_THRESHOLD) {
      predictedToReach100 += 1;
    }
  }

  return {
    totalCohorts: cohorts.length,
    actualAtOrAbove100,
    predictedToReach100,
  };
}

export function maxMobForCohortSheet(cohortSheet) {
  if (!cohortSheet?.cohorts?.length) {
    return 0;
  }
  return cohortSheet.cohorts.reduce((maxMob, row) => {
    const rowMax = (row.periods ?? []).reduce(
      (mobMax, period) => Math.max(mobMax, Number(period.mob) + 1),
      0
    );
    return Math.max(maxMob, rowMax);
  }, 0);
}

export function getCohortRoasWindowStats(cohortSheet, avgCohorts) {
  const cohorts = cohortSheet?.cohorts;
  const windowSize = Math.max(1, Math.floor(Number(avgCohorts) || 0));
  if (!cohorts?.length || windowSize < 1) {
    return {
      avgCohorts: windowSize,
      avgVectorPct: [],
      avgStdevPct: null,
    };
  }

  const maxMob = maxMobForCohortSheet(cohortSheet);
  const start = Math.max(0, cohorts.length - windowSize);
  const avgVectorPct = new Array(maxMob).fill(0);
  const perMobStdevPct = [];

  for (let mob = 0; mob < maxMob; mob += 1) {
    const values = collectRoasValuesForMob(cohorts, start, mob);
    if (!values.length) {
      continue;
    }
    const mean =
      values.reduce((sum, value) => sum + value, 0) / values.length;
    avgVectorPct[mob] = mean * 100;
    const stdev = sampleStdev(values);
    if (stdev != null) {
      perMobStdevPct.push(stdev * 100);
    }
  }

  const avgStdevPct = perMobStdevPct.length
    ? perMobStdevPct.reduce((sum, value) => sum + value, 0) /
      perMobStdevPct.length
    : null;

  return {
    avgCohorts: windowSize,
    avgVectorPct,
    avgStdevPct,
  };
}

export function getCohortRoasAvgWindows(
  cohortSheet,
  {
    minAvg = COHORT_ROAS_AVG_WINDOW_MIN,
    maxAvg = COHORT_ROAS_AVG_WINDOW_MAX,
  } = {}
) {
  const cohortCount = cohortSheet?.cohorts?.length ?? 0;
  const minWindow = Math.max(1, Math.floor(Number(minAvg) || 0));
  const requestedMax = Math.max(minWindow, Math.floor(Number(maxAvg) || 0));
  const maxWindow = Math.min(requestedMax, cohortCount);
  if (maxWindow < minWindow) {
    return [];
  }
  const windows = [];
  for (let avgCohorts = minWindow; avgCohorts <= maxWindow; avgCohorts += 1) {
    windows.push(getCohortRoasWindowStats(cohortSheet, avgCohorts));
  }
  return windows;
}

export function getGenericRoasVectorPct(
  cohortSheet,
  avgCohorts = DEFAULT_ROAS_AVG_COHORTS
) {
  if (cohortSheet?.avgRoasVectorPct?.length) {
    return cohortSheet.avgRoasVectorPct;
  }
  return getCohortRoasWindowStats(cohortSheet, avgCohorts).avgVectorPct;
}

export function sumPredictedIncrementalTriangle(cohortSheet) {
  const cohorts = cohortSheet?.cohorts;
  if (!cohorts?.length) {
    return 0;
  }

  let total = 0;
  for (const row of cohorts) {
    for (const period of row.periods ?? []) {
      if (!period.isPredicted) {
        continue;
      }
      const prevPeriod =
        period.mob > 0 ? findPeriodByMob(row.periods, period.mob - 1) : null;
      const incremental = getCohortPeriodValue(
        period,
        prevPeriod,
        'revenue',
        'incremental'
      );
      total += Number(incremental) || 0;
    }
  }
  return total;
}

export function calcIncrementalRunoff(cohortSheet) {
  const cohorts = cohortSheet?.cohorts;
  if (!cohorts?.length) {
    return {
      anchorCohortDate: null,
      firstPredictedMob: null,
      runoffByMob: [],
    };
  }

  const lastRowIndex = cohorts.length - 1;
  const lastRow = cohorts[lastRowIndex];
  const firstPredictedMob = (lastRow.periods ?? []).find(
    (period) => period.isPredicted
  )?.mob;
  if (firstPredictedMob == null) {
    return {
      anchorCohortDate: lastRow.cohortDate ?? null,
      firstPredictedMob: null,
      runoffByMob: [],
    };
  }

  const maxMob = maxMobForCohortSheet(cohortSheet);
  const runoffByMob = [];

  for (let mob = firstPredictedMob; mob < maxMob; mob += 1) {
    let sum = 0;
    for (let k = 0; ; k += 1) {
      const rowIndex = lastRowIndex - k;
      const periodMob = mob + k;
      if (rowIndex < 0 || periodMob >= maxMob) {
        break;
      }
      const row = cohorts[rowIndex];
      const period = findPeriodByMob(row.periods, periodMob);
      if (!period) {
        break;
      }
      const prevPeriod =
        periodMob > 0 ? findPeriodByMob(row.periods, periodMob - 1) : null;
      const incremental = getCohortPeriodValue(
        period,
        prevPeriod,
        'revenue',
        'incremental'
      );
      sum += Number(incremental) || 0;
    }
    runoffByMob.push({ mob, revenue: sum });
  }

  return {
    anchorCohortDate: lastRow.cohortDate ?? null,
    firstPredictedMob,
    runoffByMob,
  };
}

export function sumRunoffRevenueForMonths(runoffByMob, monthCount) {
  if (!runoffByMob?.length || monthCount < 1) {
    return 0;
  }
  return runoffByMob
    .slice(0, monthCount)
    .reduce((sum, entry) => sum + (Number(entry.revenue) || 0), 0);
}

export function getCohortSummary(cohortSheet) {
  const genericRoasVectorPct = getGenericRoasVectorPct(cohortSheet);
  const roas100Counts = getCohortRoas100Counts(cohortSheet);
  const runoff = calcIncrementalRunoff(cohortSheet);
  const runoffTotalFromVector = runoff.runoffByMob.reduce(
    (sum, entry) => sum + (Number(entry.revenue) || 0),
    0
  );
  const runoffTotal = sumPredictedIncrementalTriangle(cohortSheet);
  const runoff12Months = sumRunoffRevenueForMonths(runoff.runoffByMob, 12);
  const runoff24Months = sumRunoffRevenueForMonths(runoff.runoffByMob, 24);

  return {
    genericRoasVectorPct,
    roas100Counts,
    runoff,
    runoffTotal,
    runoffTotalFromVector,
    runoff12Months,
    runoff24Months,
  };
}
